/**
 * Flow Form block: multi-step forms with API integration
 * Fetches form definition from backend and renders dynamic fields.
 * Also supports static/demo mode when inline form data is provided.
 */

import { registerBlockRenderer, getBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper } from './utils';
import type { FlowFormData } from './types';
import type { FlowStepGroup, FlowField } from '../../core/api-client';
import { inlineLoaderHTML } from '../../core/loading/activity-loader';

// =============================================================================
// REQUEST DEDUPLICATION - Prevent duplicate API calls for same flow
// =============================================================================

/** Cache of in-flight and completed flow requests */
const flowRequestCache = new Map<string, Promise<FlowStepGroup | null>>();

/** Track timeout IDs for cleanup */
const cacheTimeoutIds = new Map<string, ReturnType<typeof setTimeout>>();

/** Cache TTL - clear after 30 seconds to allow retries */
const CACHE_TTL_MS = 30000;

/** Maximum cache size to prevent unbounded memory growth */
const MAX_CACHE_SIZE = 20;

/**
 * Clear all flow cache entries.
 * Called when switching workspaces to prevent stale data.
 */
export function clearAllFlowCache(): void {
  // Clear all pending timeouts
  cacheTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
  cacheTimeoutIds.clear();
  flowRequestCache.clear();
}

/**
 * Get flow step group with request deduplication.
 * Multiple renders of the same flow will share a single API call.
 *
 * @param lang optional target language code for cross-language form label translation
 */
async function getFlowStepGroupCached(
  apiClient: NonNullable<ReturnType<typeof getBlockRendererContext>['apiClient']>,
  workspaceId: string,
  flowId: string,
  groupIndex: number,
  lang?: string
): Promise<FlowStepGroup | null> {
  // Runtime safety check (TypeScript NonNullable can be bypassed)
  if (!apiClient) {
    console.error('[flow-form] getFlowStepGroupCached called with null apiClient');
    return null;
  }

  // Include lang in cache key so different language requests are cached separately
  const cacheKey = lang
    ? `${workspaceId}:${flowId}:${groupIndex}:${lang}`
    : `${workspaceId}:${flowId}:${groupIndex}`;

  // Return existing promise if in-flight or recently completed
  const cached = flowRequestCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Evict oldest entries if cache is full (FIFO eviction)
  if (flowRequestCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = flowRequestCache.keys().next().value;
    if (oldestKey) {
      flowRequestCache.delete(oldestKey);
      const timeoutId = cacheTimeoutIds.get(oldestKey);
      if (timeoutId) {
        clearTimeout(timeoutId);
        cacheTimeoutIds.delete(oldestKey);
      }
    }
  }

  // Create new request and cache it (pass lang for server-side translation)
  const request = apiClient.getFlowStepGroup(workspaceId, flowId, groupIndex, lang)
    .catch((error) => {
      console.error(`[flow-form] Failed to fetch flow ${flowId}:`, error);
      // Clear cache on error so retry can work
      flowRequestCache.delete(cacheKey);
      const timeoutId = cacheTimeoutIds.get(cacheKey);
      if (timeoutId) {
        clearTimeout(timeoutId);
        cacheTimeoutIds.delete(cacheKey);
      }
      return null;
    });

  flowRequestCache.set(cacheKey, request);

  // Clear from cache after TTL (track timeout for cleanup)
  const timeoutId = setTimeout(() => {
    flowRequestCache.delete(cacheKey);
    cacheTimeoutIds.delete(cacheKey);
  }, CACHE_TTL_MS);
  cacheTimeoutIds.set(cacheKey, timeoutId);

  return request;
}

/**
 * Clear flow request cache entry.
 * Used when retrying a failed request.
 */
function clearFlowCache(workspaceId: string, flowId: string, groupIndex: number): void {
  const cacheKey = `${workspaceId}:${flowId}:${groupIndex}`;
  flowRequestCache.delete(cacheKey);
  // Also clear associated timeout to prevent inconsistent state
  const timeoutId = cacheTimeoutIds.get(cacheKey);
  if (timeoutId) {
    clearTimeout(timeoutId);
    cacheTimeoutIds.delete(cacheKey);
  }
}

// =============================================================================
// STATIC FORM DATA TYPE (for demo/gallery mode)
// =============================================================================

interface StaticFlowFormData {
  title?: string;
  description?: string;
  fields: StaticFlowField[];
  submitLabel?: string;
}

interface StaticFlowField {
  id: string;
  label?: string;
  prompt?: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'date' | 'time' | 'select' | 'multiselect' | 'cards' | 'slider' | 'file';
  placeholder?: string;
  options?: string[] | { id: string; label: string; description?: string; image?: string }[];
  slider?: {
    min?: number;
    max?: number;
    step?: number;
    defaultValue?: number;
    prefix?: string;
    suffix?: string;
  };
  fileConfig?: {
    accept?: string;
    maxSizeMb?: number;
  };
}

/**
 * Check if data is static form data (for demo mode)
 */
function isStaticFormData(data: unknown): data is StaticFlowFormData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  // Static form data has fields array directly, not flowId
  return Array.isArray(d.fields) && !d.flowId;
}

/**
 * Convert static field to FlowField format
 */
function convertStaticField(field: StaticFlowField): FlowField {
  let options: { id: string; label: string; description?: string; image?: string }[] | undefined;

  if (field.options) {
    if (typeof field.options[0] === 'string') {
      // Convert string array to option objects
      options = (field.options as string[]).map(opt => ({ id: opt, label: opt }));
    } else {
      options = field.options as { id: string; label: string; description?: string; image?: string }[];
    }
  }

  // Convert slider config with defaults if provided
  let sliderConfig: FlowField['slider'] = undefined;
  if (field.slider) {
    sliderConfig = {
      min: field.slider.min ?? 0,
      max: field.slider.max ?? 100,
      step: field.slider.step ?? 1,
      defaultValue: field.slider.defaultValue ?? field.slider.min ?? 0,
      prefix: field.slider.prefix,
      suffix: field.slider.suffix,
    };
  }

  let fileConfigResult: FlowField['fileConfig'] = undefined;
  if (field.fileConfig) {
    fileConfigResult = field.fileConfig;
  }

  return {
    id: field.id,
    label: field.label,
    prompt: field.prompt,
    type: field.type,
    options,
    slider: sliderConfig,
    fileConfig: fileConfigResult,
  };
}

// =============================================================================
// FLOW FORM BLOCK
// =============================================================================

registerBlockRenderer('flow-form', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {

  const wrapper = createBlockWrapper('flow-form');

  // Form values storage
  const formValues: Record<string, string | string[]> = {};

  // Check if this is static/demo data
  if (isStaticFormData(data)) {
    // Render directly without API call
    const staticData = data as StaticFlowFormData;
    const stepGroup: FlowStepGroup = {
      index: 0,
      title: staticData.title || '',
      description: staticData.description,
      fields: staticData.fields.map(convertStaticField),
      submitLabel: staticData.submitLabel,
    };
    renderForm(stepGroup, wrapper, formValues, 'demo', 0, onAction);
    container.appendChild(wrapper);
    return;
  }

  // Dynamic form data - fetch from API
  // Normalize: accept both groupIndex and stepIndex (backend compat)
  const rawData = data as FlowFormData & { stepIndex?: number; lang?: string };
  const flowData: FlowFormData = {
    flowId: rawData.flowId,
    groupIndex: rawData.groupIndex ?? rawData.stepIndex ?? 0,
    workspaceId: rawData.workspaceId,
  };
  const lang = rawData.lang; // Extract language for cross-language form translation
  const ctx = getBlockRendererContext();

  // Loading state
  showLoadingState();

  container.appendChild(wrapper);

  // Fetch and render the form
  loadForm();

  function showLoadingState(): void {
    wrapper.innerHTML = `
      <div class="pms-flow-form-loading">
        ${inlineLoaderHTML('Loading form...')}
      </div>
    `;
  }

  function showErrorWithRetry(message: string): void {
    wrapper.innerHTML = `
      <div class="pms-block-error">
        <div class="pms-block-error-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <div class="pms-block-error-content">
          <div class="pms-block-error-text">${escapeHtml(message)}</div>
          <button type="button" class="pms-flow-form-retry-btn">Try Again</button>
        </div>
      </div>
    `;

    // Attach retry handler
    const retryBtn = wrapper.querySelector('.pms-flow-form-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        // Clear cache and retry
        const workspaceId = flowData.workspaceId || ctx.workspaceId;
        if (workspaceId) {
          clearFlowCache(workspaceId, flowData.flowId, flowData.groupIndex);
        }
        showLoadingState();
        loadForm();
      });
    }
  }

  async function loadForm(): Promise<void> {
    if (!ctx.apiClient || !ctx.workspaceId) {
      showErrorWithRetry('Unable to load form. Please try again.');
      return;
    }

    // Check if flow is available (if we have the list)
    // Only check if availableFlows is non-empty (backwards compatibility)
    if (ctx.availableFlows.length > 0 && !ctx.availableFlows.includes(flowData.flowId)) {
      console.warn(`[flow-form] Flow "${flowData.flowId}" not in availableFlows:`, ctx.availableFlows);
      showErrorWithRetry('Unable to load form. This form is not currently available.');
      return;
    }

    try {
      const workspaceId = flowData.workspaceId || ctx.workspaceId;

      // Use cached/deduplicated request to prevent parallel API calls for same flow
      // Pass lang for server-side cross-language form label translation
      const stepGroup = await getFlowStepGroupCached(
        ctx.apiClient,
        workspaceId,
        flowData.flowId,
        flowData.groupIndex,
        lang
      );

      if (!stepGroup) {
        showErrorWithRetry('Unable to load form. Please try again.');
        return;
      }

      renderForm(stepGroup, wrapper, formValues, flowData.flowId, flowData.groupIndex, onAction);
    } catch (error) {
      console.error('[flow-form] Exception loading flow form:', error);
      showErrorWithRetry('Unable to load form. Please try again.');
    }
  }
});

function renderForm(
  stepGroup: FlowStepGroup,
  wrapper: HTMLElement,
  formValues: Record<string, string | string[]>,
  flowId: string,
  groupIndex: number,
  onAction?: (value: string) => void
): void {
  wrapper.innerHTML = '';

  // Title
  if (stepGroup.title) {
    const title = document.createElement('div');
    title.className = 'pms-flow-form-title';
    title.textContent = stepGroup.title;
    wrapper.appendChild(title);
  }

  // Description
  if (stepGroup.description) {
    const desc = document.createElement('div');
    desc.className = 'pms-flow-form-desc';
    desc.textContent = stepGroup.description;
    wrapper.appendChild(desc);
  }

  // Fields
  const fieldsContainer = document.createElement('div');
  fieldsContainer.className = 'pms-flow-form-fields';

  for (const field of stepGroup.fields) {
    const fieldEl = renderFlowField(field, formValues, (fieldId, value) => {
      formValues[fieldId] = value;
    });
    fieldsContainer.appendChild(fieldEl);
  }

  wrapper.appendChild(fieldsContainer);

  // Actions container with proper spacing
  const actionsContainer = document.createElement('div');
  actionsContainer.className = 'pms-flow-form-actions';

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'button';
  submitBtn.className = 'pms-flow-form-submit pms-interactive-action-btn pms-action-primary';
  submitBtn.textContent = stepGroup.submitLabel?.trim() || 'Continue';

  submitBtn.addEventListener('click', () => {
    if (onAction) {
      // Format: flow_submit:flowId:groupIndex:field1=value1;field2=value2
      // URL-encode values to prevent delimiter collisions (;, =, :)
      const valueString = Object.entries(formValues)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => {
          const encodedValue = Array.isArray(v)
            ? v.map(item => encodeURIComponent(item)).join(',')
            : encodeURIComponent(String(v));
          return `${encodeURIComponent(k)}=${encodedValue}`;
        })
        .join(';');
      onAction(`flow_submit:${flowId}:${groupIndex}:${valueString}`);
    }
  });

  actionsContainer.appendChild(submitBtn);
  wrapper.appendChild(actionsContainer);

  // Add vertical-specific decorative elements for immersive conversion forms
  addVerticalDecorations(wrapper, stepGroup);
}

// =============================================================================
// VERTICAL DECORATIONS — Industry-native form aesthetics
// =============================================================================

/**
 * Add vertical-specific decorative DOM elements to the flow form.
 * Creates industry-native aesthetics: receipt tickets, terminal panels,
 * checkout badges, clinical intake forms, etc.
 */
function addVerticalDecorations(wrapper: HTMLElement, stepGroup: FlowStepGroup): void {
  const vertical = getBlockRendererContext().vertical;
  if (!vertical) return;

  // ── Restaurant: Order ticket / receipt ──────────────────────────────
  if (vertical === 'restaurant') {
    // Perforated tear edge (top)
    const tearTop = document.createElement('div');
    tearTop.className = 'pms-flow-tear-edge';
    wrapper.insertBefore(tearTop, wrapper.firstChild);

    // Order number
    const orderHeader = document.createElement('div');
    orderHeader.className = 'pms-flow-order-header';
    const orderNum = String(Math.floor(Math.random() * 9000) + 1000);
    orderHeader.innerHTML =
      `<span class="pms-flow-order-label">ORDER</span>` +
      `<span class="pms-flow-order-num">#${orderNum}</span>`;
    const titleEl = wrapper.querySelector('.pms-flow-form-title');
    if (titleEl) {
      wrapper.insertBefore(orderHeader, titleEl);
    }

    // Dashed separators between fields
    const fields = wrapper.querySelectorAll('.pms-flow-field');
    fields.forEach((field, i) => {
      if (i > 0) {
        const sep = document.createElement('div');
        sep.className = 'pms-flow-receipt-sep';
        field.parentNode?.insertBefore(sep, field);
      }
    });

    // Receipt footer with "THANK YOU"
    const footer = document.createElement('div');
    footer.className = 'pms-flow-receipt-footer';
    footer.innerHTML =
      '<div class="pms-flow-receipt-divider"></div>' +
      '<div class="pms-flow-receipt-thanks">THANK YOU</div>';
    wrapper.appendChild(footer);

    // Perforated tear edge (bottom)
    const tearBottom = document.createElement('div');
    tearBottom.className = 'pms-flow-tear-edge pms-flow-tear-bottom';
    wrapper.appendChild(tearBottom);
  }

  // ── SaaS / Tech: Terminal config panel ─────────────────────────────
  if (vertical === 'saas' || vertical === 'tech') {
    // Terminal header with traffic-light dots
    const termBar = document.createElement('div');
    termBar.className = 'pms-flow-terminal-bar';
    termBar.innerHTML =
      '<div class="pms-flow-terminal-dots">' +
        '<span></span><span></span><span></span>' +
      '</div>' +
      '<span class="pms-flow-terminal-title">~ configure</span>';
    wrapper.insertBefore(termBar, wrapper.firstChild);

    // Status line at bottom
    const statusLine = document.createElement('div');
    statusLine.className = 'pms-flow-status-line';
    const count = stepGroup.fields.length;
    statusLine.innerHTML =
      `<span>${count} field${count !== 1 ? 's' : ''}</span>` +
      '<span class="pms-flow-status-ready">READY</span>';
    const actions = wrapper.querySelector('.pms-flow-form-actions');
    if (actions) {
      wrapper.insertBefore(statusLine, actions);
    }
  }

  // ── Ecommerce: Secure checkout ─────────────────────────────────────
  if (vertical === 'ecommerce') {
    const badge = document.createElement('div');
    badge.className = 'pms-flow-secure-badge';
    badge.innerHTML =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">' +
        '<rect x="3" y="11" width="18" height="11" rx="2"/>' +
        '<path d="M7 11V7a5 5 0 0110 0v4"/>' +
      '</svg>' +
      '<span>SECURE CHECKOUT</span>';
    wrapper.insertBefore(badge, wrapper.firstChild);
  }

  // ── Healthcare: Patient intake ─────────────────────────────────────
  if (vertical === 'healthcare') {
    // Medical cross icon
    const cross = document.createElement('div');
    cross.className = 'pms-flow-medical-cross';
    wrapper.insertBefore(cross, wrapper.firstChild);

    // CONFIDENTIAL watermark
    const watermark = document.createElement('div');
    watermark.className = 'pms-flow-confidential';
    watermark.textContent = 'CONFIDENTIAL';
    wrapper.appendChild(watermark);
  }

  // ── Finance / Fintech: Application form ────────────────────────────
  if (vertical === 'finance' || vertical === 'fintech') {
    // Document reference number
    const ref = document.createElement('div');
    ref.className = 'pms-flow-doc-ref';
    const refNum = String(Math.floor(Math.random() * 900000) + 100000);
    ref.textContent = `REF-${refNum}`;
    wrapper.insertBefore(ref, wrapper.firstChild);

    // Security seal watermark
    const seal = document.createElement('div');
    seal.className = 'pms-flow-security-seal';
    wrapper.appendChild(seal);
  }

  // ── Real Estate: Property inquiry ──────────────────────────────────
  if (vertical === 'real_estate') {
    // Blueprint grid overlay
    const grid = document.createElement('div');
    grid.className = 'pms-flow-blueprint-grid';
    wrapper.appendChild(grid);

    // Inquiry reference
    const ref = document.createElement('div');
    ref.className = 'pms-flow-inquiry-ref';
    const refNum = String(Math.floor(Math.random() * 9000) + 1000);
    ref.textContent = `INQ-${refNum}`;
    wrapper.insertBefore(ref, wrapper.firstChild);
  }

  // ── Automotive: Vehicle configurator ───────────────────────────────
  if (vertical === 'automotive') {
    // Racing stripe accent at top
    const stripe = document.createElement('div');
    stripe.className = 'pms-flow-racing-stripe';
    wrapper.insertBefore(stripe, wrapper.firstChild);
  }

  // ── Portfolio: Creative brief ──────────────────────────────────────
  if (vertical === 'portfolio') {
    // Crop marks (corner brackets)
    const crops = document.createElement('div');
    crops.className = 'pms-flow-crop-marks';
    wrapper.appendChild(crops);

    // Brief number
    const brief = document.createElement('div');
    brief.className = 'pms-flow-brief-number';
    const num = String(Math.floor(Math.random() * 900) + 100);
    brief.textContent = `No. ${num}`;
    wrapper.insertBefore(brief, wrapper.firstChild);
  }

  // ── Professional Services: Consultation request ────────────────────
  if (vertical === 'professional_services') {
    // Letterhead accent bar
    const letterhead = document.createElement('div');
    letterhead.className = 'pms-flow-letterhead';
    wrapper.insertBefore(letterhead, wrapper.firstChild);
  }

  // ── Agency: Creative brief ─────────────────────────────────────
  if (vertical === 'agency') {
    // Campaign reference number
    const ref = document.createElement('div');
    ref.className = 'pms-flow-campaign-ref';
    const refNum = String(Math.floor(Math.random() * 9000) + 1000);
    ref.textContent = `BRIEF-${refNum}`;
    wrapper.insertBefore(ref, wrapper.firstChild);

    // Broadcast accent bar
    const accent = document.createElement('div');
    accent.className = 'pms-flow-broadcast-accent';
    wrapper.insertBefore(accent, wrapper.firstChild);
  }

  // ── Dental: Appointment card (receipt-level depth) ──────────────
  if (vertical === 'dental') {
    // Top accent bar (mint-green gradient)
    const accent = document.createElement('div');
    accent.className = 'pms-flow-dental-accent';
    wrapper.insertBefore(accent, wrapper.firstChild);

    // Appointment header (like restaurant's ORDER label + number)
    const apptHeader = document.createElement('div');
    apptHeader.className = 'pms-flow-dental-header';
    const refNum = String(Math.floor(Math.random() * 9000) + 1000);
    apptHeader.innerHTML =
      `<span class="pms-flow-dental-label">APPOINTMENT</span>` +
      `<span class="pms-flow-dental-ref">APT-${refNum}</span>`;
    const titleEl = wrapper.querySelector('.pms-flow-form-title');
    if (titleEl) {
      wrapper.insertBefore(apptHeader, titleEl);
    }

    // Dashed separators between fields (like restaurant receipt seps)
    const fields = wrapper.querySelectorAll('.pms-flow-field');
    fields.forEach((field, i) => {
      if (i > 0) {
        const sep = document.createElement('div');
        sep.className = 'pms-flow-dental-sep';
        field.parentNode?.insertBefore(sep, field);
      }
    });

    // Footer with confirmation message (like restaurant's "THANK YOU")
    const footer = document.createElement('div');
    footer.className = 'pms-flow-dental-footer';
    footer.innerHTML =
      '<div class="pms-flow-dental-divider"></div>' +
      '<div class="pms-flow-dental-confirm">YOUR SMILE, OUR PRIORITY</div>';
    wrapper.appendChild(footer);
  }

  // ── Education: Worksheet header ───────────────────────────────────
  if (vertical === 'education') {
    const worksheetBar = document.createElement('div');
    worksheetBar.className = 'pms-flow-worksheet-bar';
    worksheetBar.innerHTML = '<span>\u270E Assignment</span>';
    wrapper.insertBefore(worksheetBar, wrapper.firstChild);
  }

  // ── Nonprofit: Mission header ─────────────────────────────────────
  if (vertical === 'nonprofit') {
    const missionBar = document.createElement('div');
    missionBar.className = 'pms-flow-mission-bar';
    missionBar.innerHTML = '<span>\u2665 Your Impact</span>';
    wrapper.insertBefore(missionBar, wrapper.firstChild);
  }

  // ── Legal: Filing header ──────────────────────────────────────────
  if (vertical === 'legal') {
    const filingBar = document.createElement('div');
    filingBar.className = 'pms-flow-filing-bar';
    filingBar.innerHTML = '<span>\u00A7 Filing</span>';
    wrapper.insertBefore(filingBar, wrapper.firstChild);
  }

  // ── Recruitment: Application form ────────────────────────────────
  if (vertical === 'recruitment') {
    // Application reference number
    const ref = document.createElement('div');
    ref.className = 'pms-flow-app-ref';
    const refNum = String(Math.floor(Math.random() * 9000) + 1000);
    ref.textContent = `APP-${refNum}`;
    wrapper.insertBefore(ref, wrapper.firstChild);

    // Professional accent bar
    const accent = document.createElement('div');
    accent.className = 'pms-flow-recruitment-accent';
    wrapper.insertBefore(accent, wrapper.firstChild);
  }
}

/**
 * Render a flow field input element.
 */
function renderFlowField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void
): HTMLElement {
  const fieldWrapper = document.createElement('div');
  fieldWrapper.className = 'pms-flow-field';

  // Prompt/label
  const fieldInputId = `pms-flow-field-${field.id}`;
  if (field.prompt || field.label) {
    const label = document.createElement('label');
    label.className = 'pms-flow-field-label';
    label.textContent = field.prompt || field.label || '';
    label.htmlFor = fieldInputId;
    fieldWrapper.appendChild(label);
  }

  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'date':
    case 'time':
      renderTextField(field, formValues, onFieldChange, fieldWrapper);
      break;

    case 'textarea':
      renderTextareaField(field, formValues, onFieldChange, fieldWrapper);
      break;

    case 'select':
      renderSelectField(field, formValues, onFieldChange, fieldWrapper);
      break;

    case 'multiselect':
      renderMultiselectField(field, formValues, onFieldChange, fieldWrapper);
      break;

    case 'cards':
      renderCardsField(field, formValues, onFieldChange, fieldWrapper);
      break;

    case 'slider':
      renderSliderField(field, formValues, onFieldChange, fieldWrapper);
      break;

    case 'file':
      renderFileField(field, formValues, onFieldChange, fieldWrapper);
      break;

    default:
      // Fallback: treat unknown types as text input
      renderTextField(field, formValues, onFieldChange, fieldWrapper);
      break;
  }

  return fieldWrapper;
}

function renderTextField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  const input = document.createElement('input');
  // Use native input types for better mobile keyboards (email, tel, date, time)
  const htmlType = (['email', 'tel', 'date', 'time'] as string[]).includes(field.type)
    ? field.type
    : 'text';
  input.type = htmlType;
  input.id = `pms-flow-field-${field.id}`;
  input.className = 'pms-flow-input';
  input.placeholder = field.label || 'Enter text...';
  input.value = (formValues[field.id] as string) || '';
  input.addEventListener('input', () => {
    onFieldChange(field.id, input.value);
  });
  container.appendChild(input);
}

function renderTextareaField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  const textarea = document.createElement('textarea');
  textarea.id = `pms-flow-field-${field.id}`;
  textarea.className = 'pms-flow-input pms-flow-textarea';
  textarea.placeholder = field.label || 'Enter text...';
  textarea.rows = 3;
  textarea.value = (formValues[field.id] as string) || '';
  textarea.addEventListener('input', () => {
    onFieldChange(field.id, textarea.value);
  });
  container.appendChild(textarea);
}

function renderSelectField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  // Custom dropdown wrapper for full dark theme support
  const dropdownWrapper = document.createElement('div');
  dropdownWrapper.className = 'pms-flow-dropdown';

  // Current selection button
  const dropdownTrigger = document.createElement('button');
  dropdownTrigger.type = 'button';
  dropdownTrigger.className = 'pms-flow-dropdown-trigger';

  // Find selected option, using id if available or falling back to label
  const selectedOpt = (field.options || []).find(opt => {
    const optId = opt.id || opt.label;
    return optId && optId === formValues[field.id];
  });
  dropdownTrigger.innerHTML = `
    <span class="pms-flow-dropdown-text">${escapeHtml(selectedOpt?.label || `Select ${field.label || 'option'}...`)}</span>
    <svg class="pms-flow-dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  `;

  // Dropdown options list
  const dropdownList = document.createElement('div');
  dropdownList.className = 'pms-flow-dropdown-list';

  // Default/placeholder option
  const defaultItem = document.createElement('button');
  defaultItem.type = 'button';
  defaultItem.className = 'pms-flow-dropdown-item';
  defaultItem.textContent = `Select ${field.label || 'option'}...`;
  defaultItem.addEventListener('click', () => {
    dropdownTrigger.querySelector('.pms-flow-dropdown-text')!.textContent = `Select ${field.label || 'option'}...`;
    onFieldChange(field.id, '');
    closeDropdown();
  });
  dropdownList.appendChild(defaultItem);

  // Add options
  for (const opt of (field.options || [])) {
    // Use opt.id if available, otherwise fall back to label as id
    const optId = opt.id || opt.label;
    const item = document.createElement('button');
    item.type = 'button';
    const isSelected = optId && formValues[field.id] === optId;
    item.className = `pms-flow-dropdown-item ${isSelected ? 'pms-selected' : ''}`;
    item.textContent = opt.label;
    item.addEventListener('click', () => {
      // Update selection
      dropdownTrigger.querySelector('.pms-flow-dropdown-text')!.textContent = opt.label;
      dropdownList.querySelectorAll('.pms-flow-dropdown-item').forEach(i => i.classList.remove('pms-selected'));
      item.classList.add('pms-selected');
      onFieldChange(field.id, optId);
      closeDropdown();
    });
    dropdownList.appendChild(item);
  }

  // Toggle dropdown
  let isOpen = false;

  // Store reference to document click handler for cleanup
  const handleDocumentClick = (e: MouseEvent) => {
    if (isOpen && !dropdownWrapper.contains(e.target as Node)) {
      closeDropdown();
    }
  };

  function openDropdown() {
    isOpen = true;
    dropdownWrapper.classList.add('pms-open');
    dropdownTrigger.setAttribute('aria-expanded', 'true');

    // Smart positioning: open upward if not enough space below
    dropdownWrapper.classList.remove('pms-dropup');
    const triggerRect = dropdownTrigger.getBoundingClientRect();
    // Find the widget container boundary (shadow host or viewport)
    const widgetRoot = dropdownWrapper.closest('.pms-chat-messages')
      || dropdownWrapper.closest('.pms-panel')
      || dropdownWrapper.closest('[class*="pms-"]');
    const bottomBound = widgetRoot
      ? widgetRoot.getBoundingClientRect().bottom
      : window.innerHeight;
    const spaceBelow = bottomBound - triggerRect.bottom;
    if (spaceBelow < 180) {
      dropdownWrapper.classList.add('pms-dropup');
    }

    // Add listener only when opened (reduces global listeners)
    document.addEventListener('click', handleDocumentClick);
  }
  function closeDropdown() {
    isOpen = false;
    dropdownWrapper.classList.remove('pms-open');
    dropdownTrigger.setAttribute('aria-expanded', 'false');
    // Remove listener when closed to prevent memory leaks
    document.removeEventListener('click', handleDocumentClick);
  }

  dropdownTrigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  });

  // Keyboard navigation
  dropdownTrigger.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDropdown();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen) closeDropdown();
      else openDropdown();
    }
  });

  dropdownWrapper.appendChild(dropdownTrigger);
  dropdownWrapper.appendChild(dropdownList);
  container.appendChild(dropdownWrapper);
}

function renderMultiselectField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  const multiselectWrapper = document.createElement('div');
  multiselectWrapper.className = 'pms-flow-multiselect';

  const selectedValues = new Set<string>(
    Array.isArray(formValues[field.id]) ? formValues[field.id] as string[] : []
  );

  for (const opt of (field.options || [])) {
    // Use opt.id if available, otherwise fall back to label as id
    const optId = opt.id || opt.label;
    const optBtn = document.createElement('button');
    optBtn.type = 'button';
    optBtn.className = `pms-flow-option-btn ${optId && selectedValues.has(optId) ? 'pms-selected' : ''}`;
    optBtn.textContent = opt.label;

    optBtn.addEventListener('click', () => {
      if (selectedValues.has(optId)) {
        selectedValues.delete(optId);
        optBtn.classList.remove('pms-selected');
      } else {
        selectedValues.add(optId);
        optBtn.classList.add('pms-selected');
      }
      onFieldChange(field.id, Array.from(selectedValues));
    });

    multiselectWrapper.appendChild(optBtn);
  }

  container.appendChild(multiselectWrapper);
}

function renderCardsField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  const cardsGrid = document.createElement('div');
  cardsGrid.className = 'pms-flow-cards';

  for (const opt of (field.options || [])) {
    // Use opt.id if available, otherwise fall back to label as id
    const optId = opt.id || opt.label;
    const card = document.createElement('button');
    card.type = 'button';
    // Only mark as selected if both optId and formValue are defined and equal
    const isSelected = optId && formValues[field.id] === optId;
    card.className = `pms-flow-card ${isSelected ? 'pms-selected' : ''}`;

    let cardHtml = '';
    if (opt.image) {
      cardHtml += `<img src="${escapeHtml(opt.image)}" alt="" class="pms-flow-card-image" loading="lazy" decoding="async" />`;
    }
    cardHtml += `<div class="pms-flow-card-label">${escapeHtml(opt.label)}</div>`;
    if (opt.description) {
      cardHtml += `<div class="pms-flow-card-desc">${escapeHtml(opt.description)}</div>`;
    }
    card.innerHTML = cardHtml;

    card.addEventListener('click', () => {
      // Deselect others
      cardsGrid.querySelectorAll('.pms-flow-card').forEach(c => c.classList.remove('pms-selected'));
      card.classList.add('pms-selected');
      onFieldChange(field.id, optId);
    });

    cardsGrid.appendChild(card);
  }

  container.appendChild(cardsGrid);
}

function renderSliderField(
  field: FlowField,
  formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  const sliderWrapper = document.createElement('div');
  sliderWrapper.className = 'pms-flow-slider-wrapper';

  const slider = field.slider;
  const sliderInput = document.createElement('input');
  sliderInput.type = 'range';
  sliderInput.className = 'pms-flow-slider';
  sliderInput.min = String(slider?.min ?? 0);
  sliderInput.max = String(slider?.max ?? 100);
  sliderInput.step = String(slider?.step ?? 1);
  sliderInput.value = String(formValues[field.id] || slider?.defaultValue || slider?.min || 0);

  const sliderValue = document.createElement('div');
  sliderValue.className = 'pms-flow-slider-value';

  function updateSliderDisplay() {
    const val = parseFloat(sliderInput.value);
    let display = '';
    if (slider?.prefix) display += slider.prefix;
    display += val.toLocaleString();
    if (slider?.suffix) display += slider.suffix;
    sliderValue.textContent = display;
  }

  sliderInput.addEventListener('input', () => {
    updateSliderDisplay();
    onFieldChange(field.id, sliderInput.value);
  });

  updateSliderDisplay();
  sliderWrapper.appendChild(sliderInput);
  sliderWrapper.appendChild(sliderValue);
  container.appendChild(sliderWrapper);
}

function renderFileField(
  field: FlowField,
  _formValues: Record<string, string | string[]>,
  onFieldChange: (fieldId: string, value: string | string[]) => void,
  container: HTMLElement
): void {
  const dropzone = document.createElement('div');
  dropzone.className = 'pms-flow-file-dropzone';

  const accept = field.fileConfig?.accept || '.pdf,.docx,.txt';
  const maxSizeMb = field.fileConfig?.maxSizeMb || 10;

  // Hidden file input
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = accept;
  fileInput.className = 'pms-flow-file-input';
  fileInput.style.display = 'none';
  fileInput.id = `pms-flow-field-${field.id}`;

  // Dropzone content
  const dropzoneContent = document.createElement('div');
  dropzoneContent.className = 'pms-flow-file-content';
  dropzoneContent.innerHTML = `
    <svg class="pms-flow-file-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
    <div class="pms-flow-file-text">Drop your file here or <span class="pms-flow-file-browse">browse</span></div>
    <div class="pms-flow-file-hint">Accepted: ${accept.replace(/\./g, '').toUpperCase()} (max ${maxSizeMb}MB)</div>
  `;

  // File selected state
  const fileSelected = document.createElement('div');
  fileSelected.className = 'pms-flow-file-selected';
  fileSelected.style.display = 'none';

  function handleFile(file: File): void {
    // Empty file check
    if (file.size === 0) {
      fileSelected.innerHTML = `
        <div class="pms-flow-file-error">File is empty. Please select a valid file.</div>
      `;
      fileSelected.style.display = 'block';
      dropzoneContent.style.display = 'none';
      return;
    }

    // Size check
    if (file.size > maxSizeMb * 1024 * 1024) {
      fileSelected.innerHTML = `
        <div class="pms-flow-file-error">File too large. Maximum size is ${maxSizeMb}MB.</div>
      `;
      fileSelected.style.display = 'block';
      dropzoneContent.style.display = 'none';
      return;
    }

    // Extension check
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const acceptedExts = accept.split(',').map(a => a.trim().toLowerCase());
    if (!acceptedExts.includes(ext)) {
      fileSelected.innerHTML = `
        <div class="pms-flow-file-error">File type not accepted. Please upload: ${accept}</div>
      `;
      fileSelected.style.display = 'block';
      dropzoneContent.style.display = 'none';
      return;
    }

    // Read file as data URL (handles binary PDF/DOCX correctly)
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the "data:...;base64," prefix to get raw base64
      const base64Content = dataUrl.substring(dataUrl.indexOf(',') + 1);
      // Encode filename to prevent ':' or other delimiters from breaking the format
      const safeName = encodeURIComponent(file.name);
      onFieldChange(field.id, `file:${safeName}:${base64Content}`);

      // Show selected state
      fileSelected.innerHTML = `
        <div class="pms-flow-file-info">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          <span class="pms-flow-file-name">${escapeHtml(file.name)}</span>
          <span class="pms-flow-file-size">(${(file.size / 1024).toFixed(0)}KB)</span>
        </div>
        <button type="button" class="pms-flow-file-remove">Remove</button>
      `;
      fileSelected.style.display = 'block';
      dropzoneContent.style.display = 'none';

      // Attach remove handler
      const removeBtn = fileSelected.querySelector('.pms-flow-file-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          onFieldChange(field.id, '');
          fileInput.value = '';
          fileSelected.style.display = 'none';
          dropzoneContent.style.display = '';
        });
      }
    };
    reader.readAsDataURL(file);
  }

  // Click to browse
  dropzone.addEventListener('click', () => {
    fileInput.click();
  });

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) handleFile(file);
  });

  // Drag and drop
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('pms-flow-file-dragover');
  });

  dropzone.addEventListener('dragleave', () => {
    dropzone.classList.remove('pms-flow-file-dragover');
  });

  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('pms-flow-file-dragover');
    const file = e.dataTransfer?.files[0];
    if (file) handleFile(file);
  });

  dropzone.appendChild(fileInput);
  dropzone.appendChild(dropzoneContent);
  dropzone.appendChild(fileSelected);
  container.appendChild(dropzone);
}

// Also register for FLOW_FORM marker format
registerBlockRenderer('flow_form', (data, container, onAction) => {
  const renderer = getBlockRenderer('flow-form');
  if (renderer) renderer(data, container, onAction);
});
